import type { Entry } from '../types';

export interface CategoryMeta {
  /** Lucide icon name (see src/icons.ts). */
  icon: string;
  label: string;
}

export const CATEGORY_META: Record<string, CategoryMeta> = {
  documents: { icon: 'file-text', label: 'Documents' },
  images: { icon: 'image', label: 'Images' },
  media: { icon: 'clapperboard', label: 'Media' },
  privacy: { icon: 'shield', label: 'Privacy' },
  files: { icon: 'folder', label: 'Files' },
  'data-explorers': { icon: 'chart-column', label: 'Data Explorers' },
  'live-dashboards': { icon: 'activity', label: 'Live Dashboards' },
  science: { icon: 'flask-conical', label: 'Science' },
  emulator: { icon: 'monitor', label: 'Emulator' },
  fun: { icon: 'sparkles', label: 'Fun' },
  'ios-apps': { icon: 'smartphone', label: 'iOS Apps' },
  // Ben's own products (type: 'product', src/data/products.json)
  finance: { icon: 'wallet', label: 'Finance' },
  events: { icon: 'ticket', label: 'Events' },
  networking: { icon: 'wifi', label: 'Networking' },
  home: { icon: 'home', label: 'Home' },
  // Game genres (type: 'game' — fed by gh-game-factory)
  arcade: { icon: 'zap', label: 'Arcade' },
  puzzle: { icon: 'puzzle', label: 'Puzzle' },
  board: { icon: 'grid-3x3', label: 'Board' },
  card: { icon: 'layers', label: 'Card & Dice' },
  word: { icon: 'brain', label: 'Word & Trivia' },
  strategy: { icon: 'target', label: 'Strategy' },
  party: { icon: 'dices', label: 'Party' },
};

export const FALLBACK_META: CategoryMeta = { icon: 'folder', label: 'Other' };

/** Maps legacy tags[0] category values (pre-backfill index entries) to curated categories. */
const LEGACY_TAG_MAP: Record<string, string> = {
  'image-tools': 'images',
  'encryption-privacy': 'privacy',
  'media-tools': 'media',
  'file-tools': 'files',
  'document-tools': 'documents',
  'p2p-transfer': 'files',
};

/** Resolve the curated category for a raw entry that may predate the icon/category schema. */
export function resolveCategory(
  explicit: string | undefined,
  tags: string[],
  type: Entry['type'],
): string {
  if (explicit && CATEGORY_META[explicit]) return explicit;
  if (type === 'app') return 'ios-apps';
  if (type === 'game') return 'arcade';
  const legacy = tags[0] ? LEGACY_TAG_MAP[tags[0]] : undefined;
  if (legacy) return legacy;
  return type === 'web' ? 'data-explorers' : 'files';
}

export function metaFor(category: string): CategoryMeta {
  return CATEGORY_META[category] ?? FALLBACK_META;
}
